import type { Book } from '../lib/api';

/**
 * The order book.
 *
 * Both columns show *bids* — what people are willing to pay for YES and for NO.
 * That is how the engine actually holds them, and it is also the honest way to
 * present a binary market: a YES bid at 60 and a NO bid at 40 are two people
 * agreeing on the same price from opposite directions. Rendering it as a
 * conventional bid/ask ladder would require inverting one side and would
 * mislead anyone who then tried to reason about their own orders.
 *
 * Colour identifies the side, but never alone: each column is headed by a text
 * label, and the key above spells both out.
 */
export function DepthLadder({ book, rows = 6 }: { book: Book; rows?: number }) {
  const yes = book.YES.slice(0, rows);
  const no = book.NO.slice(0, rows);
  const max = Math.max(1, ...yes.map((l) => l.qty), ...no.map((l) => l.qty));
  const height = Math.max(yes.length, no.length);

  if (height === 0) {
    return (
      <div className="empty-state">
        No orders yet. The first bid on either side starts the book.
      </div>
    );
  }

  return (
    <>
      <div className="side-key">
        <span><i style={{ background: 'var(--yes)' }} />Bids for YES</span>
        <span><i style={{ background: 'var(--no)' }} />Bids for NO</span>
      </div>
      <table className="ladder">
        <colgroup>
          <col /><col /><col className="spacer" /><col /><col />
        </colgroup>
        <thead>
          <tr>
            <th>Price</th>
            <th>Qty</th>
            <th aria-hidden="true" />
            <th>Price</th>
            <th>Qty</th>
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: height }, (_, i) => {
            const y = yes[i];
            const n = no[i];
            return (
              <tr key={i}>
                <td className="yes">
                  {y && <span className="depth" style={{ width: `${(y.qty / max) * 100}%` }} />}
                  <span className="val price">{y ? `${y.price}%` : ''}</span>
                </td>
                <td className="yes">
                  <span className="val qty">{y ? y.qty.toLocaleString() : ''}</span>
                </td>
                <td aria-hidden="true" />
                <td className="no">
                  {n && <span className="depth" style={{ width: `${(n.qty / max) * 100}%` }} />}
                  <span className="val price">{n ? `${n.price}%` : ''}</span>
                </td>
                <td className="no">
                  <span className="val qty">{n ? n.qty.toLocaleString() : ''}</span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </>
  );
}

/** Best price available on a side right now, or null if nobody is bidding. */
export function bestPrice(book: Book, side: 'YES' | 'NO'): number | null {
  return book[side][0]?.price ?? null;
}

/**
 * What it would cost to buy `side` right now: the opposite side's best bid
 * determines it, because that is who you would trade with (100 - their price).
 * Falls back to the same side's own best bid when nobody is offering.
 */
export function askFor(book: Book, side: 'YES' | 'NO'): number | null {
  const opposite = side === 'YES' ? 'NO' : 'YES';
  const counter = book[opposite][0]?.price;
  if (counter !== undefined) return 100 - counter;
  const own = book[side][0]?.price;
  return own !== undefined ? own : null;
}
